import { Box, Button, CircularProgress, Grid, Typography } from "@mui/material";
import { Form, Formik, FormikProps } from "formik";
import { useEffect, useState } from "react";
import * as Yup from "yup";
import City from "../../models/City";
import Province from "../../models/Province";
import CandidateWork from "../../models/CandidateWork";
import Paragraph from "../../components/Paragraph";
import AppTextField from "../../components/AppTextField";
import AppPhoneField from "../../components/AppPhoneField";
import AppSelect from "../../components/AppSelect";
import ConfirmationDialog from "../../components/ConfirmationDialog";
import utilsService from "../../services/UtilsService";
import candidateService from "../../services/CandidateService";
import LoadingModal from "../../components/LoadingModal";
import { handleInputChangeToUppercase } from "../../Utils";
import { useAppSelector } from "../../store/store";
import { ApplicationState } from "../../models/ApplicationState";
import { Country } from "../../models/Country";
import IOption from "../../interfaces/IOption";

const validationScheme = Yup.object().shape({
  recentWork: Yup.string().required("Requerido"),
  position: Yup.string().required("Requerido"),
  companyDirection: Yup.string().required("Requerido"),
  state: Yup.string().required("Requerido"),
  city: Yup.string().required("Requerido"),
  salary: Yup.string().required("Requerido"),
  workTime: Yup.string().required("Requerido"),
  workTimeOut: Yup.string().required("Requerido"),
  companyPhone: Yup.string().required("Requerido"),
  workFunctionality: Yup.string().required("Requerido"),
  countryId: Yup.string().required("Requerido"),
});

interface CandidatePreviousJobModalProps {
  candidateWork: CandidateWork;
  countries: Country[];
  onSave: (candidateWork: CandidateWork) => void;
  close: () => void;
}

function CandidatePreviousJobModal({
  candidateWork,
  onSave,
  close,
  countries,
}: // city,
CandidatePreviousJobModalProps) {
  const [isSaving, setIsSaving] = useState<boolean>(false);
  var [openDialog, setOpenDialog] = useState(false);
  var [dialogTitle, setDialogTitle] = useState("");
  var [dialogDesc, setDialogDesc] = useState("");
  var [dialogButton, setDialogButton] = useState<
    JSX.Element | JSX.Element[] | undefined
  >();
  const [isValidForm, setIsValidForm] = useState<boolean>(false);
  const [cities, setCities] = useState<City[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [provinces, setProvinces] = useState<Province[]>([]);
  const appState: ApplicationState = useAppSelector((state) => state.appState);
  const [initialValues, setInitialValues] = useState<CandidateWork>({
    isCurrentlyWorking: false,
    candidateWorkId: 0,
    recentWork: "",
    position: "",
    salary: 0,
    companyDirection: "",
    state: "",
    city: "",
    companyNeighborhood: "",
    workTime: "",
    companyPhone: "",
    companyPhoneCountryCode: appState.countryCode,
    supervisorName: "",
    actualJOB: false,
    workFunctionality: "",
  });

  async function submit(values: CandidateWork) {
    const candidateWork: CandidateWork = {
      ...values,
      isCurrentlyWorking: false,
      actualJOB: false,
    };

    try {
      setIsSaving(true);
      const work = await candidateService.saveCandidateWork(candidateWork);
      onSave(work);
      setIsSaving(false);
    } catch {
      setIsSaving(false);
      setOpenDialog(true);
      setDialogTitle("Error");
      setDialogDesc("Ha ocurrido un error. Por favor intente mas tarde");
      setDialogButton(<Button onClick={() => setOpenDialog(false)}>Ok</Button>);
    }
  }

  async function initialData() {
    //const provs = await utilsService.getProvinces(appState.countryCode);
    // const cit = await utilsService.getCities(
    //   candidateWork.provinceId!.toString(),
    // );

    setInitialValues({
      ...candidateWork,
      workTimeOut:
        candidateWork.workTimeOut != null ? candidateWork.workTimeOut : "",
      workTime: candidateWork.workTime != null ? candidateWork.workTime : "",
      workFunctionality: candidateWork.workFunctionality ?? "",
      companyPhoneCountryCode:
        candidateWork.companyPhoneCountryCode || appState.countryCode,
    });

    //setProvinces(provs);
    //setCities(cit);
    setIsLoading(false);
  }

  useEffect(() => {
    initialData();
  }, []);

  return (
    <Box position="relative">
      {isLoading ? (
        <Box display="block" margin="auto" textAlign="center">
          <CircularProgress color="secondary" size={50} sx={{ mr: "10px" }} />
          <Typography variant="body1">Cargando...</Typography>
        </Box>
      ) : (
        <Formik
          initialValues={initialValues}
          onSubmit={submit}
          validationSchema={validationScheme}
          validateOnMount={true}
        >
          {(props: FormikProps<CandidateWork>) => {
            const {
              values,
              touched,
              errors,
              handleBlur,
              handleChange,
              isSubmitting,
              setFieldValue,
              isValid,
            } = props;

            return (
              <Form>
                <Grid container spacing={1}>
                  {/* Company Name*/}

                  <Grid item lg={4} xs={12}>
                    <Paragraph title="Nombre completo de la empresa" required />
                    <AppTextField
                      error={
                        errors.recentWork && touched.recentWork ? true : false
                      }
                      name="recentWork"
                      value={values.recentWork}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        handleInputChangeToUppercase(event, setFieldValue)
                      }
                    />
                  </Grid>

                  {/* Position*/}

                  <Grid item lg={4} xs={12}>
                    <Paragraph title="Posición" required />
                    <AppTextField
                      error={errors.position && touched.position ? true : false}
                      name="position"
                      value={values.position}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        handleInputChangeToUppercase(event, setFieldValue)
                      }
                    />
                  </Grid>

                  {/* Salario*/}

                  <Grid item lg={4} xs={12}>
                    <Paragraph
                      title={`Salario mensual ( ${appState.countryExchangeRateName} )`}
                      required
                    />
                    <AppTextField
                      type="number"
                      error={errors.salary && touched.salary ? true : false}
                      name="salary"
                      value={values.salary}
                      onChange={handleChange}
                    />
                  </Grid>

                  {/* Address*/}

                  <Grid item lg={4} xs={12}>
                    <Paragraph title="Dirección 1 (calle y número)" required />
                    <AppTextField
                      error={
                        errors.companyDirection && touched.companyDirection
                          ? true
                          : false
                      }
                      name="companyDirection"
                      value={values.companyDirection}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        handleInputChangeToUppercase(event, setFieldValue)
                      }
                    />
                  </Grid>

                  <Grid item lg={4} xs={12}>
                    <Paragraph title="Dirección 2 (apto., edificio, referencia)" />
                    <AppTextField
                      name="companyDirection2"
                      value={values.companyDirection2}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        handleInputChangeToUppercase(event, setFieldValue)
                      }
                    />
                  </Grid>
                  {/* Add Country  */}
                  <Grid item lg={4} xs={12}>
                    <Paragraph title="País" required />
                    <AppSelect
                      name="countryId"
                      error={
                        errors.countryId && touched.countryId ? true : false
                      }
                      value={values.countryId}
                      onChange={handleChange}
                      fullWidth
                      isSearchable={true}
                      options={countries.map((el) => {
                        var option: IOption = {
                          label: el.name,
                          value: el.countryId.toString(),
                        };
                        return option;
                      })}
                    />
                  </Grid>

                  {/* Provincia*/}

                  <Grid item lg={4} xs={12}>
                    <Paragraph title="Provincia/Estado/Región" required />
                    <AppTextField
                      error={errors.state && touched.state ? true : false}
                      name="state"
                      value={values.state}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        handleInputChangeToUppercase(event, setFieldValue)
                      }
                    />
                  </Grid>

                  {/* City */}

                  <Grid item lg={4} xs={12}>
                    <Paragraph title="Ciudad/Localidad" required />
                    <AppTextField
                      error={errors.city && touched.city ? true : false}
                      name="city"
                      value={values.city}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        handleInputChangeToUppercase(event, setFieldValue)
                      }
                    />
                  </Grid>

                  {/* Codigo postal */}

                  <Grid item lg={4} xs={12}>
                    <Paragraph title="Código postal (opcional)" />
                    <AppTextField
                      name="companyNeighborhood"
                      value={values.companyNeighborhood}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        handleInputChangeToUppercase(event, setFieldValue)
                      }
                    />
                  </Grid>

                  {/* Fecha inicio */}

                  <Grid item lg={4} xs={12}>
                    <Paragraph title="Fecha de inicio" required />
                    <AppTextField
                      type="date"
                      error={errors.workTime && touched.workTime ? true : false}
                      name="workTime"
                      value={values.workTime}
                      onChange={handleChange}
                    />
                  </Grid>

                  {/* Fecha salida */}

                  <Grid item lg={4} xs={12}>
                    <Paragraph title="Fecha de salida" required />
                    <AppTextField
                      type="date"
                      error={
                        errors.workTimeOut && touched.workTimeOut ? true : false
                      }
                      name="workTimeOut"
                      value={values.workTimeOut}
                      onChange={handleChange}
                    />
                  </Grid>

                  {/* Telefono */}

                  <Grid item lg={4} xs={12}>
                    <Paragraph title="Teléfono" required />
                    <AppPhoneField
                      countries={countries}
                      name="companyPhone"
                      error={
                        errors.companyPhone && touched.companyPhone
                          ? true
                          : false
                      }
                      countryId={
                        countries.find(
                          (country) =>
                            country.countryAlpha2 ===
                            values.companyPhoneCountryCode,
                        )?.countryId
                      }
                      phone={values.companyPhone ?? ""}
                      onBlur={handleBlur}
                      onCountryChange={(countryId) => {
                        const country = countries.find(
                          (item) => item.countryId.toString() === countryId,
                        );
                        setFieldValue(
                          "companyPhoneCountryCode",
                          country?.countryAlpha2 ?? "",
                        );
                      }}
                      onPhoneChange={(phone) => {
                        setFieldValue("companyPhone", phone);
                      }}
                    />
                  </Grid>

                  {/* Describe work functionality */}

                  <Grid item lg={8} xs={12}>
                    <Paragraph
                      title="Describa brevemente sus funciones "
                      required
                    />
                    <AppTextField
                      error={
                        errors.workFunctionality && touched.workFunctionality
                          ? true
                          : false
                      }
                      name="workFunctionality"
                      value={values.workFunctionality}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        handleInputChangeToUppercase(event, setFieldValue)
                      }
                    />
                  </Grid>
                  {/* BUTTON */}
                  <Grid item xs={12}>
                    <Grid item xs={12}>
                      <Button
                        onClick={close}
                        color="customGrey"
                        variant="contained"
                        sx={{ mr: "10px", color: "white" }}
                      >
                        Cerrar
                      </Button>
                      <Button
                        onClick={() => {
                          if (!isValid) {
                            setIsValidForm(true);
                          }
                        }}
                        type="submit"
                        color="secondary"
                        variant="contained"
                        sx={{ bgcolor: "secondary.main" }}
                      >
                        Guardar
                      </Button>
                    </Grid>
                  </Grid>

                  <ConfirmationDialog
                    maxWidth="sm"
                    open={isValidForm}
                    title={"Error"}
                    description={"Llenar todos los requisitos en rojo"}
                    buttons={
                      <Button onClick={() => setIsValidForm(false)}>Ok</Button>
                    }
                  />

                  <LoadingModal
                    isLoading={isSaving}
                    loadingMessage="Guardando"
                  />

                  <ConfirmationDialog
                    maxWidth="sm"
                    open={openDialog}
                    title={dialogTitle}
                    description={dialogDesc}
                    buttons={dialogButton}
                  />
                </Grid>
              </Form>
            );
          }}
        </Formik>
      )}
    </Box>
  );
}

export default CandidatePreviousJobModal;
